import { getValues } from '@morph-mapper/utils';
import type {
  EdgeInstance,
  NodeId,
  NodeInstance,
  PortId,
} from '../../../types/graph';
import { DeferredCompositeCommand } from '../composite';
import { CommandErrorKind, type GraphCommand } from '../types';
import { AddEdgeCommand, FailCommand, RemoveEdgeCommand } from '../elementary';
import { createAddNodeCommand } from '../elementary/add-node';
import {
  deleteGraphsForRemovedNodes,
  restoreGraphsForAddedNodes,
  withEffects,
} from '../effects';
import { RemoveNodeCommand } from '../elementary/remove-node';
import { defaultIdFactory } from '../utils';

export interface ReplaceNodeCommandProps {
  nodeId: NodeId;
  created: {
    // Id of input port of the created node to which we are connecting.
    inputPortId?: PortId;
    // Id of output port of the created node to which we are connecting.
    outputPortId?: PortId;
  };
  // Details of node to be created.
  node: Omit<NodeInstance, 'id'>;
}

export const createReplaceNodeCommand = ({
  nodeId,
  created,
  node,
}: ReplaceNodeCommandProps) => {
  return new DeferredCompositeCommand((graph) => {
    const newNodeId = defaultIdFactory.nodeId();
    const touchingEdges = getValues(graph.edges).filter(
      (edge) => edge.from.nodeId === nodeId || edge.to.nodeId === nodeId
    );
    const removeEdgeCommands = touchingEdges.map(
      (edge) => new RemoveEdgeCommand(edge.id)
    );
    const rewiredEdges: GraphCommand[] = [];
    for (const edge of touchingEdges) {
      let from = edge.from;
      let to = edge.to;

      if (edge.from.nodeId === nodeId) {
        if (!created.outputPortId) {
          return [
            new FailCommand({
              kind: CommandErrorKind.Invariant,
              message: `Missing outputPortId for replacing node ${nodeId}`,
            }),
          ];
        }
        from = {
          ...edge.from,
          nodeId: newNodeId,
          portId: created.outputPortId,
        };
      }

      if (edge.to.nodeId === nodeId) {
        if (!created.inputPortId) {
          return [
            new FailCommand({
              kind: CommandErrorKind.Invariant,
              message: `Missing inputPortId for replacing node ${nodeId}`,
            }),
          ];
        }
        to = {
          ...edge.to,
          nodeId: newNodeId,
          portId: created.inputPortId,
        };
      }

      const nextEdge: EdgeInstance = {
        ...edge,
        from,
        to,
      };
      rewiredEdges.push(new AddEdgeCommand(nextEdge));
    }

    return [
      ...removeEdgeCommands,
      withEffects(
        new RemoveNodeCommand(nodeId),
        ({ before, after }) => deleteGraphsForRemovedNodes(before, after),
        ({ inverse }) =>
          withEffects(inverse, ({ before, after }) =>
            restoreGraphsForAddedNodes(before, after)
          )
      ),
      createAddNodeCommand({ ...node, id: newNodeId }),
      ...rewiredEdges,
    ];
  }, 'Replace node');
};
